import { Component, computed, contentChild, DestroyRef, effect, inject, Injector, input, output, signal, untracked } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { FieldTree, form, FormField, FormRoot } from '@angular/forms/signals';
import { lastValueFrom, Observable, Subscription } from 'rxjs';
import {
  DmAlert,
  DmButton,
  DmButtonGroup,
  DmButtonGroupItem,
  DmCombobox,
  DmFileupload,
  DmIcon,
  DmInput,
  DmModal,
  DmModalFooter,
  DmModalHeader,
  DmSelect,
  DmSelectItem,
} from '@digitale-menschen/component-library-angular';
import {
  DmFileUploadItem,
  DmFileUploadItemAction,
  DmFileUploadItemRename,
  dmFileUploadResultInterface,
} from '@digitale-menschen/component-library';
import { API_CONFIGURATION, environmentUser } from '@lexplus-environments';
import { ConfirmDialogService } from '../../services/confirm-dialog.service';
import { fieldErrorMessage } from '../../functions/field-error-message.function';
import { notify } from '../../utilities/notify.utility';
import {
  getLexplusCreateUploadDtoInitialValue,
  LexplusCreateUploadDto,
  lexplusCreateUploadDtoSchema,
  LexplusSuccessDto,
  LexplusTagMiniDto,
  LexplusUpdateUploadDto,
  LexplusUploadDokumentTypMiniDto,
  LexplusUploadLimitsDto,
  LexplusUploadStagingDto,
  tagsControllerFindAllV1,
  uploadsControllerCreateV1,
  uploadsControllerDeleteDateiV1,
  uploadsControllerUpdateV1,
} from '@lexplus-user-api';
import {
  uploadsControllerCreateV1 as adminUploadsControllerCreateV1,
  uploadsControllerDeleteDateiV1 as adminUploadsControllerDeleteDateiV1,
  uploadsControllerFindTagsV1 as adminUploadsControllerFindTagsV1,
  uploadsControllerUpdateV1 as adminUploadsControllerUpdateV1,
  uploadDokumentTypeControllerFindV1,
} from '@lexplus-admin-api';
import { resolveDokumentTypLabel } from '../../models/dokument-typ-display.model';
import { DokumentDateiUploadService } from '../../services/dokument-datei-upload.service';
import { DokumentUploadPickDirective } from './dokument-upload-pick.directive';

/**
 * Which segment is showing; also decides what the submit does. `pick` links documents that already
 * exist and is offered only when the consumer projects a {@link DokumentUploadPickDirective}.
 */
type UploadModus = 'pick' | 'datei' | 'link';

/**
 * Which API the dialog records the document through.
 *
 * - `user` — the user app's Dokumenten-Manager: file first, form afterwards, with tags, the
 *   link tab, the duplicate warning and edit mode.
 * - `admin` — the admin app: the same dialog and the same two-step flow, against the admin API,
 *   for the Kunde in {@link DokumentUploadModalComponent.kundeId} (and, when editing, the Standort in
 *   {@link DokumentUploadModalComponent.standortId}).
 */
export type DokumentUploadApi = 'user' | 'admin';

/**
 * What the dialog needs of a document to edit it. The user API's detail DTO satisfies it, and so
 * does the admin API's list row, which carries no tags.
 */
export interface DokumentUploadEditable {
  uploadID: string;
  dateiname: string;
  dateiTyp: string;
  beschreibung?: string | null;
  externerLink?: string | null;
  dokumentTyp?: LexplusUploadDokumentTypMiniDto | null;
  dokumentSubTyp?: LexplusUploadDokumentTypMiniDto | null;
  tags?: { name: string }[];
}

/**
 * A document the dialog wrote: its id, the type the user left it with and the subtype they
 * picked (`null` when none).
 */
export interface DokumentUploadResult {
  uploadId: string;
  dokumentTyp: LexplusUploadDokumentTypMiniDto | null;
  dokumentSubTyp: LexplusUploadDokumentTypMiniDto | null;
}

/** One file of the upload segment, from its pick to the staged bytes a create refers to. */
interface StagedUploadFile {
  /** Unique within the dialog, so a row's delete and retry find it even when two files share a name. */
  id: string;
  file: File;
  progress: number;
  status: NonNullable<DmFileUploadItem['status']>;
  error?: string;
  /** The server's handle for the bytes, once they arrived. */
  staging: LexplusUploadStagingDto | null;
  /**
   * The name of the document this file becomes, typed in its row and validated by the same
   * generated schema as the dialog's own name field. Only its `dateiname` is read.
   */
  nameForm: FieldTree<LexplusCreateUploadDto>;
  /** What its row shows: the last valid name given to it, with the file's extension. */
  displayName: string;
}

/**
 * A file's name as a document name: without its extension, as the design's example
 * («Nachweis Ölheizung 2026») writes it.
 *
 * @param {string} fileName - The file's name
 * @returns {string} The name without its extension
 */
function stripFileExtension(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, '');
}

/**
 * A document name as its file's row shows it: with the file's extension, which the row keeps fixed.
 *
 * @param {string} dateiname - The document name, without an extension
 * @param {string} fileName - The file's name
 * @returns {string} The name with the file's extension
 */
function buildDisplayName(dateiname: string, fileName: string): string {
  return `${dateiname}${fileName.slice(stripFileExtension(fileName).length)}`;
}

/**
 * A byte count in the unit the dropzone's hint and the progress row read in. Deliberately
 * decimal MB (1000-based), matching what the design writes («max. 10mb», «6.4 MB of 16 MB»)
 * and what a file manager shows the user for the same file.
 *
 * @param {number} bytes - The size to name
 * @returns {string} The size with its unit
 */
function formatBytes(bytes: number): string {
  if (bytes < 1000) {
    return `${bytes} B`;
  }
  if (bytes < 1000 * 1000) {
    return `${Math.round(bytes / 1000)} KB`;
  }
  const megabytes = bytes / (1000 * 1000);
  // One decimal below 10 MB, none above: «6.4 MB» but «50 MB».
  return `${megabytes < 10 ? megabytes.toFixed(1) : Math.round(megabytes)} MB`;
}

/**
 * «Neues Dokument» — and, with an `upload` supplied, the same form in edit mode.
 *
 * Shared by both apps; `api` decides which backend it talks to (see {@link DokumentUploadApi}).
 *
 * A consumer that adds documents to something, such as a requirement, renames the dialog through
 * `heading`, `description` and `submitLabel`, may open it on a file it already holds
 * (`initialFiles`), and may put a picker of existing documents first by projecting an
 * `<ng-template lpDokumentUploadPick>`. That segment submits through `pickAction`.
 *
 * The upload is **two steps**: the bytes go up as soon as the file is picked, so the design's
 * live progress bar has something to show, and the form is submitted afterwards carrying the
 * staging handle the first step returned. That is also what makes the duplicate warning
 * possible before submit: the staging response already says whether a file of that name
 * exists.
 *
 * Both modes share the create DTO's model and schema, even when the submit ends up as a
 * `PATCH`: the update DTO's generated schema is empty (every field optional), so binding the
 * form to it would drop the required/maxLength/pattern rules on the file name.
 */
@Component({
  selector: 'lp-dokument-upload-modal',
  imports: [
    DmModal,
    DmModalHeader,
    DmModalFooter,
    DmButton,
    DmButtonGroup,
    DmButtonGroupItem,
    DmIcon,
    DmInput,
    DmCombobox,
    DmSelect,
    DmSelectItem,
    DmFileupload,
    DmAlert,
    FormRoot,
    FormField,
    NgTemplateOutlet,
  ],
  templateUrl: './dokument-upload-modal.component.html',
})
export class DokumentUploadModalComponent {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfiguration = inject(API_CONFIGURATION);
  private readonly uploadService = inject(DokumentDateiUploadService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly destroyRef = inject(DestroyRef);
  /** Lets a file's name form be built outside the injection context, when the file is picked. */
  private readonly injector = inject(Injector);

  public readonly isModalOpen = input<boolean>(false);
  /** The document being edited; `null` opens the dialog in create mode. */
  public readonly upload = input<DokumentUploadEditable | null>(null);
  /** The server's size and type limits, so the hint and the client-side check share one source. */
  public readonly limits = input<LexplusUploadLimitsDto | null>(null);
  /** Which API the document is recorded through. */
  public readonly api = input<DokumentUploadApi>('user');
  /** The Kunde a document created through the admin API belongs to; unused by the user API. */
  public readonly kundeId = input<number | null>(null);
  /** The Standort a document is filed under: a created one is filed there, and the admin API scopes an edit to it. */
  public readonly standortId = input<number | null>(null);
  /** The create mode's heading; «Neues Dokument» when not set. */
  public readonly heading = input<string | null>(null);
  /** The create mode's description; the upload-or-link hint when not set. */
  public readonly description = input<string | null>(null);
  /** The create mode's submit label, in every segment; «Dokument erfassen» when not set. */
  public readonly submitLabel = input<string | null>(null);
  /** Files to upload as the dialog opens, such as ones dropped onto a card; a single-file dialog takes the first. */
  public readonly initialFiles = input<File[]>([]);
  /** Whether a create takes several files at once, one document each; without it a second file replaces the first. */
  public readonly multipleFiles = input<boolean>(false);
  /** Whether the pick segment holds a selection to submit. */
  public readonly canSubmitPick = input<boolean>(false);
  /**
   * Submits the pick segment. Resolves `true` when the dialog may close, `false` to keep it open;
   * it reports its own failures.
   */
  public readonly pickAction = input<(() => Promise<boolean>) | null>(null);
  /** Whether a created document is confirmed by a toast; off for a consumer that confirms the next step instead. */
  public readonly notifyOnCreate = input<boolean>(true);

  /** Emits `true` when a document was written, so the overview knows to reload. */
  public readonly closeClick = output<boolean>();
  /** Emits the document the dialog created, before it closes. */
  public readonly created = output<DokumentUploadResult>();
  /** Every document one submit created, reported once; a single-file create reports one. */
  public readonly createdAll = output<DokumentUploadResult[]>();
  /** Emits the document the dialog edited, before it closes. */
  public readonly updated = output<DokumentUploadResult>();

  protected readonly pickSegment = contentChild(DokumentUploadPickDirective);

  private readonly createModel = signal(getLexplusCreateUploadDtoInitialValue());
  protected readonly uploadForm = form(this.createModel, lexplusCreateUploadDtoSchema, {
    submission: { action: () => this.save() },
  });

  protected readonly contentMounted = signal<boolean>(false);
  protected readonly modus = signal<UploadModus>('datei');
  /** The files of the upload segment, in the order they were picked. */
  protected readonly stagedFiles = signal<StagedUploadFile[]>([]);
  /** Whether a document of this name already exists, from staging or from a rejected submit. */
  protected readonly duplicateWarning = signal<boolean>(false);
  /** The root types the admin API offers; empty for the user API, which has no type picker. */
  protected readonly dokumentTypes = signal<LexplusUploadDokumentTypMiniDto[]>([]);
  /** The children of the selected type; empty while no type is selected or it has none. */
  protected readonly dokumentSubTypes = signal<LexplusUploadDokumentTypMiniDto[]>([]);
  /** Kept outside the form: the DTO field is optional, so it has no `Field` to bind. */
  protected readonly dokumentTypID = signal<string | null>(null);
  protected readonly dokumentSubTypID = signal<string | null>(null);
  protected readonly dokumentTypOptions = computed(() => this.toSelectOptions(this.dokumentTypes()));
  protected readonly dokumentSubTypOptions = computed(() => this.toSelectOptions(this.dokumentSubTypes()));
  protected readonly tagOptions = signal<LexplusTagMiniDto[]>([]);
  protected readonly isTagsLoading = signal<boolean>(false);
  protected readonly isSaving = signal<boolean>(false);

  protected readonly isEditMode = computed<boolean>(() => this.upload() !== null);
  /** A create may take several files; an edit replaces the one file it has. */
  private readonly acceptsSeveralFiles = computed<boolean>(() => this.multipleFiles() && !this.isEditMode());
  /** Several files make several documents, so the description field gives way. */
  protected readonly hasSeveralFiles = computed<boolean>(() => this.stagedFiles().length > 1);
  /**
   * A file being created is named in its row, so the name field is left to the link segment and to an
   * edit, where no row names the document.
   */
  protected readonly namesInRows = computed<boolean>(() => this.modus() === 'datei' && !this.isEditMode());
  /** Each file names its own document, so each name has to be valid. */
  private readonly allFileNamesValid = computed<boolean>(() => this.stagedFiles().every((staged) => staged.nameForm.dateiname().valid()));
  /** The progress rows, as the dropzone takes them. */
  protected readonly files = computed<DmFileUploadItem[]>(() =>
    this.stagedFiles().map(
      (staged): DmFileUploadItem => ({
        id: staged.id,
        name: staged.displayName,
        nameError: (this.namesInRows() && fieldErrorMessage(staged.nameForm.dateiname(), this.dateinameRequiredMessage)) || undefined,
        size: staged.file.size,
        progress: staged.progress,
        status: staged.status,
        error: staged.error,
        filetype: staged.file.type,
      }),
    ),
  );
  private readonly hasStagedBytes = computed<boolean>(() => this.stagedFiles().some((staged) => staged.staging !== null));
  /** A create waits until every file it lists has arrived. */
  private readonly allFilesStaged = computed<boolean>(
    () => this.stagedFiles().length > 0 && this.stagedFiles().every((staged) => staged.staging !== null),
  );
  /** Whether the dialog offers the pick segment; an edit always concerns the one document. */
  protected readonly hasPickSegment = computed<boolean>(() => this.pickSegment() !== undefined && !this.isEditMode());
  protected readonly segments = computed<{ modus: UploadModus; label: string }[]>(() => [
    ...(this.hasPickSegment() ? [{ modus: 'pick' as const, label: this.tabPickLabel }] : []),
    { modus: 'datei', label: this.tabDateiLabel },
    { modus: 'link', label: this.tabLinkLabel },
  ]);
  protected readonly activeSegmentIndex = computed<number>(() => this.segments().findIndex((segment) => segment.modus === this.modus()));
  protected readonly headingText = computed<string>(() => (this.isEditMode() ? this.editHeading : (this.heading() ?? this.createHeading)));
  protected readonly descriptionText = computed<string>(() =>
    this.isEditMode() ? this.editDescription : (this.description() ?? this.createDescription),
  );
  protected readonly primaryLabel = computed<string>(() =>
    this.isEditMode() ? this.saveLabel : (this.submitLabel() ?? this.createSubmitLabel),
  );
  protected readonly isAdmin = computed<boolean>(() => this.api() === 'admin');
  protected readonly maxDateigroesse = computed<number | undefined>(() => this.limits()?.maxDateigroesseBytes);
  protected readonly mimetypes = computed<string>(() => this.limits()?.erlaubteMimetypen.join(', ') ?? '*');

  /** «max. 50 MB» — the number comes from the API so there is only one place to change it. */
  protected readonly dropzoneHint = computed<string>(() => {
    const maxSize = this.maxDateigroesse();
    if (!maxSize) {
      return '';
    }
    const groesse = formatBytes(maxSize);
    return $localize`:@@dokumente.upload.maxGroesse:max. ${groesse}:groesse:`;
  });

  protected readonly tagNames = computed<string[]>(() => this.uploadForm.tags().value());
  /**
   * Several files share one set of tags, which the field says below itself wherever several files can
   * be added — before they are, so the tags are chosen knowing it. Empty rather than `undefined`
   * elsewhere: the `dm-*` wrappers skip an `undefined` write, which would leave the hint standing.
   */
  protected readonly tagsHint = computed<string>(() =>
    this.acceptsSeveralFiles() && this.modus() === 'datei' ? this.tagsForAllFilesHint : '',
  );

  protected readonly dateinameErrorMessage = computed<string | undefined>(() =>
    fieldErrorMessage(this.uploadForm.dateiname(), this.dateinameRequiredMessage),
  );

  /**
   * Whether an edit differs from the stored document. Compares values rather than the form's
   * dirty flag, so typing a change and typing it back counts as no change.
   */
  private readonly hasChanges = computed<boolean>(() => {
    const editedUpload = this.upload();
    if (!editedUpload) {
      return true;
    }
    const formValue = this.uploadForm().value();
    return (
      this.hasStagedBytes() ||
      this.modus() !== (editedUpload.dateiTyp === 'link' ? 'link' : 'datei') ||
      formValue.dateiname !== editedUpload.dateiname ||
      (formValue.beschreibung ?? '') !== (editedUpload.beschreibung ?? '') ||
      (formValue.externerLink ?? '') !== (editedUpload.externerLink ?? '') ||
      formValue.tags.join('\n') !== (editedUpload.tags ?? []).map((tag) => tag.name).join('\n') ||
      this.dokumentTypID() !== (editedUpload.dokumentTyp?.uploadDokumentTypID ?? null) ||
      this.dokumentSubTypID() !== (editedUpload.dokumentSubTyp?.uploadDokumentTypID ?? null)
    );
  });

  /**
   * Whether the dialog may be submitted. Beyond the schema, the file tab needs bytes that
   * finished uploading and the link tab needs a URL — neither is expressible in a schema
   * shared by both tabs. An edit additionally needs something to change.
   */
  protected readonly canSave = computed<boolean>(() => {
    if (this.modus() === 'pick') {
      return this.canSubmitPick() && this.pickAction() !== null && !this.isSaving();
    }
    const namesValid = this.namesInRows() ? this.allFileNamesValid() : this.uploadForm().valid();
    if (!namesValid || this.isSaving() || !this.hasChanges()) {
      return false;
    }
    // The admin API scopes every write to a Kunde, and an edit to the Standort as well.
    if (this.isAdmin() && (this.kundeId() === null || (this.isEditMode() && this.standortId() === null))) {
      return false;
    }
    // A subtype is only valid under the type it was loaded for; `dokumentSubTypes` holds exactly
    // the children of the selected type, so a stale or not-yet-loaded one is rejected here. The
    // user API has no type picker and sends the stored subtype untouched.
    const subTypID = this.dokumentSubTypID();
    if (
      this.isAdmin() &&
      subTypID !== null &&
      (this.dokumentTypID() === null || !this.dokumentSubTypes().some((subType) => subType.uploadDokumentTypID === subTypID))
    ) {
      return false;
    }
    if (this.modus() === 'link') {
      return (this.uploadForm.externerLink?.()?.value() ?? '').trim().length > 0;
    }
    // In edit mode the stored file stays unless a new one replaces it — but a document that
    // is an external reference has no stored file, so turning it into one needs bytes. Without
    // this the save would send neither source and silently leave it a link.
    const hasStoredFile = this.isEditMode() && this.upload()?.dateiTyp !== 'link';
    return this.allFilesStaged() || hasStoredFile;
  });

  private readonly isModalOpenEffect = effect(() => {
    if (this.isModalOpen()) {
      // Only the open flag may re-run this: seeding reads and writes form state, and a re-run
      // would overwrite what the user has edited since.
      untracked(() => {
        this.contentMounted.set(true);
        this.seedFromUpload();
        this.openInitialSegment();
        void this.searchTags('');
        void this.loadDokumentTypes();
      });
    }
  });

  private readonly dateinameRequiredMessage = $localize`:@@dokumente.upload.dateinameErforderlich:Bitte geben Sie einen Dateinamen an.`;
  private readonly genericErrorTitle = $localize`:@@common.errors.generic:Ein Fehler ist aufgetreten`;
  private readonly genericErrorDescription = $localize`:@@common.errors.genericDescription:Bitte überprüfen Sie Ihre Eingaben und versuchen Sie es erneut.`;
  protected readonly createHeading = $localize`:@@dokumente.upload.titel:Neues Dokument`;
  protected readonly createDescription = $localize`:@@dokumente.upload.beschreibung:Laden Sie eine Datei hoch oder hinterlegen Sie einen Dateipfad`;
  protected readonly editHeading = $localize`:@@dokumente.bearbeiten.titel:Dokument bearbeiten`;
  protected readonly editDescription = $localize`:@@dokumente.bearbeiten.beschreibung:Passen Sie die Angaben an, ersetzen Sie die Datei oder die Verlinkung`;
  protected readonly segmentsLabel = $localize`:@@dokumente.upload.segmente:Dokument hinzufügen als`;
  protected readonly tabPickLabel = $localize`:@@dokumente.upload.tabAuswahl:Dokumenten-Manager`;
  protected readonly tabDateiLabel = $localize`:@@dokumente.upload.tabDatei:Datei hochladen`;
  protected readonly tabLinkLabel = $localize`:@@dokumente.upload.tabLink:Dateipfad erfassen`;
  protected readonly uploadCtaLabel = $localize`:@@dokumente.upload.cta:Zum Hochladen klicken`;
  protected readonly dragDropLabel = $localize`:@@dokumente.upload.dragDrop:oder per Drag-and-Drop hierher ziehen`;
  protected readonly uploadingLabel = $localize`:@@dokumente.upload.wirdHochgeladen:Wird hochgeladen`;
  protected readonly completeLabel = $localize`:@@dokumente.upload.hochgeladen:Hochgeladen`;
  protected readonly failedLabel = $localize`:@@dokumente.upload.fehlgeschlagen:Fehlgeschlagen`;
  protected readonly failedMessage = $localize`:@@dokumente.upload.fehlgeschlagenHinweis:Upload fehlgeschlagen, bitte erneut versuchen`;
  protected readonly tryAgainLabel = $localize`:@@dokumente.upload.erneutVersuchen:Erneut versuchen`;
  protected readonly deleteFileLabel = $localize`:@@dokumente.upload.dateiEntfernen:Datei entfernen`;
  protected readonly renameFileLabel = $localize`:@@dokumente.upload.dateiUmbenennen:Name des Dokuments für`;
  protected readonly dateinameLabel = $localize`:@@dokumente.feld.dateiname:Dateiname`;
  protected readonly descriptionLabel = $localize`:@@common.beschreibung:Beschreibung`;
  protected readonly descriptionPlaceholder = $localize`:@@dokumente.feld.beschreibungPlatzhalter:Z.B. eine kurze Erklärung, was die Datei beinhaltet`;
  protected readonly dateipfadLabel = $localize`:@@dokumente.feld.dateipfad:Dateipfad`;
  protected readonly dateipfadPlaceholder = $localize`:@@dokumente.feld.dateipfadPlatzhalter:https://…`;
  protected readonly dateipfadTooltip = $localize`:@@dokumente.feld.dateipfadTooltip:Die vollständige Adresse des Ablageorts, zum Beispiel ein SharePoint-Link.`;
  protected readonly dokumentTypLabel = $localize`:@@common.typ:Typ`;
  protected readonly noDokumentTypLabel = $localize`:@@common.keineAngabe:Keine Angabe`;
  protected readonly dokumentSubTypLabel = $localize`:@@dokumente.feld.subTyp:Subtyp`;
  protected readonly tagsLabel = $localize`:@@dokumente.feld.tags:Tags`;
  protected readonly tagsTooltip = $localize`:@@dokumente.feld.tagsTooltip:Suchen Sie bestehende Tags oder erfassen Sie einen neuen.`;
  protected readonly tagsPlaceholder = $localize`:@@common.search.placeholder:Suche`;
  protected readonly tagCreateLabel = $localize`:@@dokumente.feld.tagErstellen:«{query}» erfassen`;
  protected readonly noTagsTitle = $localize`:@@dokumente.filter.keineTags:Keine Tags vorhanden`;
  protected readonly noTagsDescription = $localize`:@@dokumente.feld.tagsNeuHinweis:Tippen Sie, um einen neuen Tag zu erfassen.`;
  protected readonly cancelLabel = $localize`:@@common.cancel:Abbrechen`;
  protected readonly createSubmitLabel = $localize`:@@dokumente.upload.erfassen:Dokument erfassen`;
  protected readonly saveLabel = $localize`:@@common.save:Speichern`;
  private readonly tagsForAllFilesHint = $localize`:@@dokumente.upload.tagsFuerAlle:Die Tags gelten für alle hochgeladenen Dokumente.`;
  protected readonly duplikatHeading = $localize`:@@dokumente.duplikat.titel:Achtung, Duplikat!`;
  protected readonly duplikatText = computed<string>(() =>
    this.hasSeveralFiles()
      ? $localize`:@@dokumente.duplikat.textMehrere:Einige dieser Dateien tragen den Namen einer Datei, die Sie bereits hochgeladen haben. Trotzdem hochladen?`
      : $localize`:@@dokumente.duplikat.text:Sie haben bereits eine Datei mit demselben Namen hochgeladen. Trotzdem noch einmal hochladen?`,
  );
  protected readonly duplikatSchliessenLabel = $localize`:@@common.schliessen:Schliessen`;
  protected readonly duplikatEntfernenLabel = computed<string>(() =>
    this.hasSeveralFiles()
      ? $localize`:@@dokumente.duplikat.entfernenMehrere:Duplikate entfernen`
      : $localize`:@@dokumente.duplikat.entfernen:Neuen Upload entfernen`,
  );
  protected readonly environmentUser = environmentUser;

  /** How many tags the autocomplete offers per query. */
  private readonly tagSearchLimit = 20;

  /** The uploads in flight, by file; unsubscribing aborts one when its row's bin icon is used. */
  private readonly uploadSubscriptions = new Map<string, Subscription>();
  /** Counts file picks, so a slower earlier upload cannot write over a later one, and names each file uniquely. */
  private uploadPick = 0;
  /** Counts pick submits, so one the user has moved on from cannot close the dialog when it answers. */
  private pickRun = 0;
  /** Sequence number of the newest tag query; an older answer must not overwrite it. */
  private latestTagRequestId = 0;

  constructor() {
    this.destroyRef.onDestroy(() => this.uploadSubscriptions.forEach((subscription) => subscription.unsubscribe()));
  }

  /**
   * Switches segment and drops what the other segments hold, staged bytes included.
   *
   * @param {UploadModus} modus - The segment that was activated
   */
  protected async onModusChange(modus: UploadModus): Promise<void> {
    if (modus === this.modus()) {
      return;
    }
    this.modus.set(modus);
    this.duplicateWarning.set(false);
    this.invalidatePending();

    if (modus !== 'link') {
      this.uploadForm.externerLink?.().value.set(null);
    }
    if (modus !== 'datei') {
      await this.discardStagedFiles();
    }
  }

  /**
   * Starts uploading the picked file.
   *
   * @param {dmFileUploadResultInterface} result - The file the dropzone read
   */
  protected async onFileChange(result: dmFileUploadResultInterface): Promise<void> {
    if (!(result.data instanceof File)) {
      // `outputformat="file"` is set, so this cannot happen — but the event's type allows
      // a buffer or a data URL and silently ignoring those would be a puzzle to debug.
      notify.error(this.genericErrorTitle, { description: this.genericErrorDescription });
      return;
    }

    await this.uploadFile(result.data);
  }

  /**
   * The dropzone rejected the file for its size. It reports that itself only by not
   * accepting it, so the reason has to be said out loud — otherwise a drop looks ignored.
   */
  protected onSizeLimitExceeded(): void {
    const groesse = formatBytes(this.maxDateigroesse() ?? 0);
    notify.error($localize`:@@dokumente.upload.zuGross:Datei zu gross`, {
      description: $localize`:@@dokumente.upload.zuGrossHinweis:Die Datei überschreitet die erlaubte Grösse von ${groesse}:groesse:.`,
    });
  }

  /** The dropzone rejected the file for its type; same reasoning as the size limit. */
  protected onUnacceptedFile(): void {
    const endungen = this.limits()?.erlaubteEndungen.join(', ') ?? '';
    notify.error($localize`:@@dokumente.upload.typNichtErlaubt:Dateityp nicht erlaubt`, {
      description: $localize`:@@dokumente.upload.typNichtErlaubtHinweis:Erlaubt sind: ${endungen}:endungen:.`,
    });
  }

  /**
   * Retries a failed upload. The dropzone keeps no file, so the row goes and the user picks it again.
   *
   * @param {DmFileUploadItemAction} action - The failed row
   * @returns {Promise<void>}
   */
  protected async onRetry(action: DmFileUploadItemAction): Promise<void> {
    await this.discardStagedFiles(this.idsOf(action));
  }

  /**
   * Renames the document a file becomes. A name the schema rejects stays out of the row, which
   * keeps showing the last valid one under the error, as the row expects.
   *
   * @param {DmFileUploadItemRename} rename - The row and the name typed for it, without the extension
   */
  protected onFileRename(rename: DmFileUploadItemRename): void {
    const staged = this.stagedFiles().find((candidate) => candidate.id === rename.item.id);
    if (!staged) {
      return;
    }
    const dateiname = rename.basename.trim();
    const nameField = staged.nameForm.dateiname();
    nameField.value.set(dateiname);
    nameField.markAsTouched();
    if (nameField.valid()) {
      this.updateStagedFile(staged.id, { displayName: buildDisplayName(dateiname, staged.file.name) });
    }
  }

  /**
   * Removes a file through its row's bin icon, or every file when the dropzone resets.
   *
   * @param {DmFileUploadItemAction} action - The row whose file goes
   * @returns {Promise<void>}
   */
  protected async onFileDelete(action: DmFileUploadItemAction): Promise<void> {
    await this.discardStagedFiles(this.idsOf(action));
  }

  /**
   * The duplicate warning's «entfernen»: removes the files the server named duplicates.
   *
   * @returns {Promise<void>}
   */
  protected async onDuplicatesDelete(): Promise<void> {
    const duplicateIds = this.stagedFiles()
      .filter((staged) => staged.staging?.istDuplikat)
      .map((staged) => staged.id);
    await this.discardStagedFiles(new Set(duplicateIds));
  }

  /**
   * Answers the Tag autocomplete from the API. `dynamicSearch` leaves the filtering to us,
   * so every keystroke is a query — debounced by the combobox itself.
   *
   * @param {string} search - What the user typed
   * @returns {Promise<void>}
   */
  protected async searchTags(search: string): Promise<void> {
    const requestId = ++this.latestTagRequestId;
    this.isTagsLoading.set(true);

    try {
      const kundeId = this.kundeId();
      const response = await lastValueFrom(
        this.isAdmin() && kundeId !== null
          ? adminUploadsControllerFindTagsV1(this.httpClient, this.apiConfiguration.baseUrl, {
              kundeId,
              search: search || undefined,
              limit: this.tagSearchLimit,
            })
          : tagsControllerFindAllV1(this.httpClient, this.apiConfiguration.baseUrlUser, {
              search: search || undefined,
              limit: this.tagSearchLimit,
            }),
      );
      if (requestId !== this.latestTagRequestId) {
        return;
      }
      this.tagOptions.set(response.body);
    } catch {
      if (requestId !== this.latestTagRequestId) {
        return;
      }
      // The field still works — a tag can be typed and created — so this stays silent
      // rather than raising a toast over a dialog the user is filling in.
      this.tagOptions.set([]);
    } finally {
      if (requestId === this.latestTagRequestId) {
        this.isTagsLoading.set(false);
      }
    }
  }

  /**
   * Takes the selected tags. The combobox round-trips a real array when it was given one,
   * so a tag name containing a comma survives.
   *
   * @param {string[]} values - The selected tag names
   */
  protected onTagsChange(values: string[]): void {
    this.uploadForm.tags().value.set(values);
  }

  /**
   * A tag the user typed rather than picked. Added to the offered options so the chip has a
   * label to render; the server upserts it on submit.
   *
   * @param {string} name - The new tag's name
   */
  protected onTagCreate(name: string): void {
    const trimmed = name.trim();
    if (!trimmed || this.tagOptions().some((tag) => tag.name === trimmed)) {
      return;
    }
    // `tagID` carries the name too: the field submits names, and a placeholder id would
    // have to be told apart from a real one later.
    this.tagOptions.update((options) => [{ tagID: trimmed, name: trimmed, usages: 0 }, ...options]);
  }

  /**
   * The description, bridged by hand: `beschreibung` is optional *and* nullable on the DTO,
   * so it is a `MaybeFieldTree` rather than a `Field<string>` and `[formField]` rejects it.
   *
   * @param {string} value - The new description
   */
  protected onDescriptionChange(value: string): void {
    this.uploadForm.beschreibung?.().value.set(value || null);
  }

  /**
   * The document type, bridged like the description. Picking another type drops the subtype,
   * which belongs to the old one, and loads the new type's children.
   *
   * @param {string | null} value - The selected type's ID; empty when the selection is cleared
   * @returns {Promise<void>}
   */
  protected async onDokumentTypChange(value: string | null): Promise<void> {
    const typeID = value || null;
    if (typeID === this.dokumentTypID()) {
      return;
    }
    this.dokumentTypID.set(typeID);
    this.dokumentSubTypID.set(null);
    // Emptied before the request: the old type's children must not be pickable while the new ones load.
    this.dokumentSubTypes.set([]);
    await this.loadDokumentSubTypes(typeID);
  }

  /**
   * The document subtype; `null` when the user clears the selection.
   *
   * @param {string | null} value - The selected subtype's ID
   */
  protected onDokumentSubTypChange(value: string | null): void {
    this.dokumentSubTypID.set(value || null);
  }

  /**
   * The external link, bridged for the same reason as the description.
   *
   * @param {string} value - The new link
   */
  protected onExternerLinkChange(value: string): void {
    this.uploadForm.externerLink?.().value.set(value || null);
  }

  /**
   * Keeps the upload despite the duplicate. Dismissing the warning is the consent the
   * server asks for, so the submit carries `trotzdemAnlegen`.
   */
  protected onDuplikatAkzeptiert(): void {
    this.duplicateWarning.set(false);
    this.uploadForm.trotzdemAnlegen?.().value.set(true);
  }

  /**
   * Mirrors a close the dialog performed itself — the library closes on some escape keys
   * despite `canautoclose="false"`. Without this the parent keeps `isModalOpen` set and the
   * dialog stays unreachable. The same event bubbling up from a drawer inside the dialog is ignored.
   *
   * @param {CustomEvent<boolean>} event - The open state the dialog or a descendant reported
   */
  protected onOpenChange(event: CustomEvent<boolean>): void {
    if (event.target !== event.currentTarget || event.detail || !this.isModalOpen()) {
      return;
    }
    this.confirmDialogService.dismiss();
    this.closeClick.emit(false);
  }

  /**
   * Routes the escape key to the same close path as the close icon; the dialog's own escape
   * handling does not reach it while focus sits in a `dm-*` control.
   *
   * @param {KeyboardEvent} event - A key event from within the dialog
   */
  protected onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      return;
    }
    event.preventDefault();
    void this.onCloseClick();
  }

  /**
   * Closes the dialog, asking first when something has been entered. Anything already
   * uploaded is dropped, so the server keeps no bytes no one asked to store.
   *
   * @returns {Promise<void>}
   */
  protected async onCloseClick(): Promise<void> {
    const discardDescription =
      this.modus() === 'pick'
        ? $localize`:@@dokumente.upload.verwerfenAuswahl:Die gewählten Dokumente werden nicht hinzugefügt.`
        : $localize`:@@dokumente.upload.verwerfenBeschreibung:Das begonnene Dokument wird nicht gespeichert.`;
    if (this.hasInput() && !(await this.confirmDialogService.confirmDiscardChanges(discardDescription))) {
      return;
    }

    this.invalidatePending();
    await this.discardStagedFiles();
    this.closeClick.emit(false);
  }

  /**
   * Clears the dialog once it is closed — after the animation, so nothing disappears before
   * the dialog does.
   */
  protected onModalClosed(): void {
    this.invalidatePending();
    this.contentMounted.set(false);
    this.uploadForm().reset(getLexplusCreateUploadDtoInitialValue());
    this.stagedFiles.set([]);
    this.duplicateWarning.set(false);
    this.modus.set('datei');
    this.tagOptions.set([]);
    this.dokumentTypID.set(null);
    this.dokumentSubTypID.set(null);
    this.dokumentTypes.set([]);
    this.dokumentSubTypes.set([]);
  }

  /**
   * Writes the document. In create mode a `POST`, in edit mode a `PATCH` of the same fields.
   * A `409` means the server found a document of that name: the warning is raised rather
   * than an error toast, because retrying with `trotzdemAnlegen` is a real option.
   * In the pick segment it runs the consumer's {@link pickAction} instead.
   *
   * @returns {Promise<void>}
   */
  protected async save(): Promise<void> {
    // The enter key in the name field submits the form too.
    if (!this.canSave()) {
      return;
    }
    if (this.modus() === 'pick') {
      await this.submitPick();
      return;
    }
    this.isSaving.set(true);

    try {
      const editedUpload = this.upload();
      const body = this.buildBody(editedUpload !== null);
      if (editedUpload) {
        // The selections the PATCH carries, resolved before it is awaited: the user may change them
        // while it is in flight, and `updated` must report what was written.
        const submittedTypID = this.dokumentTypID();
        const submittedSubTypID = this.dokumentSubTypID();
        const dokumentTyp = this.resolveSubmittedDokumentTyp(this.dokumentTypes(), submittedTypID, editedUpload.dokumentTyp);
        const dokumentSubTyp = this.resolveSubmittedDokumentTyp(this.dokumentSubTypes(), submittedSubTypID, editedUpload.dokumentSubTyp);
        // `null` clears the subtype, which `undefined` would leave in place.
        await lastValueFrom(this.updateThroughApi(editedUpload.uploadID, { ...body, uploadDokumentSubTypID: submittedSubTypID }));
        this.updated.emit({ uploadId: editedUpload.uploadID, dokumentTyp, dokumentSubTyp });
        notify.success($localize`:@@dokumente.bearbeiten.success:Dokument gespeichert`, {
          description: $localize`:@@dokumente.bearbeiten.successDescription:Die Änderungen wurden übernommen.`,
        });
      } else if (this.modus() === 'datei' && this.hasSeveralFiles()) {
        await this.createSeveral(body);
        return;
      } else {
        const response = await lastValueFrom(this.createThroughApi(body));
        if (response.body.stringId) {
          const result = this.buildCreatedResult(response.body.stringId);
          this.created.emit(result);
          this.createdAll.emit([result]);
        }
        if (this.notifyOnCreate()) {
          const dateiname = body.dateiname;
          notify.success($localize`:@@dokumente.upload.success:Dokument erfasst`, {
            description: $localize`:@@dokumente.upload.successDescription:«${dateiname}:dateiname:» ist im Dokumenten Manager aufgeführt.`,
          });
        }
      }
      // The bytes belong to the document now; nothing left to clean up.
      this.stagedFiles.set([]);
      this.closeClick.emit(true);
    } catch (error) {
      if ((error as HttpErrorResponse)?.status === HttpStatusCode.Conflict) {
        this.duplicateWarning.set(true);
        return;
      }
      const serverMessage = (error as HttpErrorResponse)?.error?.message;
      notify.error(this.genericErrorTitle, { description: serverMessage ?? this.genericErrorDescription });
    } finally {
      this.isSaving.set(false);
    }
  }

  /**
   * Creates one document per file, in turn, each under the name given to its file and sharing the tags and
   * types the form holds. A refused file stops the run and the error reaches {@link save}; what was
   * created so far is reported, and those files leave the list, so the next submit takes up the rest.
   *
   * @param {LexplusCreateUploadDto} sharedBody - The payload the form builds, before the per-file fields
   * @returns {Promise<void>}
   */
  private async createSeveral(sharedBody: LexplusCreateUploadDto): Promise<void> {
    const createdResults: DokumentUploadResult[] = [];
    try {
      for (const staged of this.stagedFiles()) {
        const response = await lastValueFrom(
          this.createThroughApi({
            ...sharedBody,
            dateiname: staged.nameForm.dateiname().value().trim(),
            beschreibung: null,
            uploadStagingID: staged.staging?.uploadStagingID ?? null,
          }),
        );
        // The bytes belong to the document now; the file is done.
        this.stagedFiles.update((stagedFiles) => stagedFiles.filter((candidate) => candidate.id !== staged.id));
        if (response.body.stringId) {
          const result = this.buildCreatedResult(response.body.stringId);
          this.created.emit(result);
          createdResults.push(result);
        }
      }
    } finally {
      if (createdResults.length) {
        this.createdAll.emit(createdResults);
      }
    }

    if (this.notifyOnCreate()) {
      const count = createdResults.length;
      notify.success($localize`:@@dokumente.upload.successMehrere:Dokumente erfasst`, {
        description: $localize`:@@dokumente.upload.successMehrereBeschreibung:${count}:count: Dokumente sind im Dokumenten Manager aufgeführt.`,
      });
    }
    this.closeClick.emit(true);
  }

  /**
   * What a create reports: the new document and the types it was left with.
   *
   * @param {string} uploadId - The new document's ID
   * @returns {DokumentUploadResult} The result to emit
   */
  private buildCreatedResult(uploadId: string): DokumentUploadResult {
    return {
      uploadId,
      dokumentTyp: this.findDokumentTyp(this.dokumentTypes(), this.dokumentTypID()),
      dokumentSubTyp: this.findDokumentTyp(this.dokumentSubTypes(), this.dokumentSubTypID()),
    };
  }

  /**
   * Runs the consumer's pick submit and closes the dialog when it says so.
   *
   * @returns {Promise<void>}
   */
  private async submitPick(): Promise<void> {
    const pickAction = this.pickAction();
    if (!pickAction) {
      return;
    }
    const run = ++this.pickRun;
    this.isSaving.set(true);
    try {
      if ((await pickAction()) && run === this.pickRun) {
        this.closeClick.emit(true);
      }
    } finally {
      this.isSaving.set(false);
    }
  }

  /**
   * Invalidates a file pick waiting for a delete and a pick submit waiting for its answer, so neither
   * acts after a segment switch or a close.
   */
  private invalidatePending(): void {
    this.uploadPick++;
    this.pickRun++;
  }

  /**
   * Sends the update to the API the dialog is bound to.
   *
   * @param {string} uploadID - The document being edited
   * @param {LexplusUpdateUploadDto} body - The changes
   * @returns The request, whose body is the write's result
   */
  private updateThroughApi(uploadID: string, body: LexplusUpdateUploadDto): Observable<{ body: LexplusSuccessDto }> {
    const kundeId = this.kundeId();
    const standortId = this.standortId();
    if (this.isAdmin() && kundeId !== null && standortId !== null) {
      return adminUploadsControllerUpdateV1(this.httpClient, this.apiConfiguration.baseUrl, {
        uploadId: uploadID,
        kundeId,
        standortId,
        body,
      });
    }
    return uploadsControllerUpdateV1(this.httpClient, this.apiConfiguration.baseUrlUser, { id: uploadID, body });
  }

  /**
   * Sends the create to the API the dialog is bound to.
   *
   * @param {LexplusCreateUploadDto} body - The new document's form
   * @returns The request, whose body is the write's result
   */
  private createThroughApi(body: LexplusCreateUploadDto): Observable<{ body: LexplusSuccessDto }> {
    const kundeId = this.kundeId();
    if (this.isAdmin() && kundeId !== null) {
      // Filed under the Standort right away, so the Standort's document list shows it whatever its type.
      return adminUploadsControllerCreateV1(this.httpClient, this.apiConfiguration.baseUrl, {
        kundeId,
        standortId: this.standortId() ?? undefined,
        body,
      });
    }
    return uploadsControllerCreateV1(this.httpClient, this.apiConfiguration.baseUrlUser, { body });
  }

  /**
   * Builds the payload. The staging handle lives in {@link staging} rather than in the form —
   * the user never types it — so it has to be joined in here, and this is the one place that
   * decides which of the two mutually exclusive sources is sent.
   *
   * `create` demands **exactly one** of them: the server raises the same
   * «entweder … oder … nicht beides» for none as for both. `update` is tolerant — it only
   * touches a source that is actually present — so editing without replacing the file sends
   * neither and leaves the stored one alone.
   *
   * @param {boolean} isUpdate - Whether the payload is for a `PATCH` rather than a `POST`
   * @returns {LexplusCreateUploadDto} The body to send
   */
  private buildBody(isUpdate: boolean): LexplusCreateUploadDto {
    const formValue = this.uploadForm().value();
    const isLink = this.modus() === 'link';
    const stagingID = this.stagedFiles()[0]?.staging?.uploadStagingID ?? null;

    // On an update a source the user did not touch is left out entirely; `null` would clear
    // the stored one. On a create the unused source is an explicit `null`.
    const absent = isUpdate ? undefined : null;

    return {
      dateiname: this.namesInRows() ? (this.stagedFiles()[0]?.nameForm.dateiname().value().trim() ?? '') : formValue.dateiname,
      beschreibung: formValue.beschreibung,
      tags: formValue.tags,
      trotzdemAnlegen: formValue.trotzdemAnlegen,
      uploadDokumentTypID: this.dokumentTypID() ?? undefined,
      uploadDokumentSubTypID: this.dokumentSubTypID() ?? undefined,
      uploadStagingID: isLink ? absent : (stagingID ?? absent),
      externerLink: isLink ? formValue.externerLink : absent,
    };
  }

  /**
   * Uploads a file into the file segment. A dialog that takes one file replaces the previous one —
   * aborting an upload that is still running — and one that takes several adds it to the list.
   *
   * @param {File} file - The file to upload
   * @returns {Promise<void>}
   */
  private async uploadFile(file: File): Promise<void> {
    const pick = ++this.uploadPick;
    if (!this.acceptsSeveralFiles()) {
      // Discarding the previous file awaits a DELETE, so a second pick can start while the first
      // is still in it. Only the newest pick may write anything from here on.
      await this.discardStagedFiles();
      if (pick !== this.uploadPick) {
        return;
      }
    }

    const id = `${pick}:${file.name}`;
    const nameForm = form(
      signal({ ...getLexplusCreateUploadDtoInitialValue(), dateiname: stripFileExtension(file.name) }),
      lexplusCreateUploadDtoSchema,
      {
        injector: this.injector,
      },
    );
    this.stagedFiles.update((stagedFiles) => [
      ...stagedFiles,
      { id, file, progress: 0, status: 'uploading', staging: null, nameForm, displayName: file.name },
    ]);

    const subscription = this.uploadService.upload(file, this.isAdmin() ? this.kundeId() : null).subscribe({
      next: (event) => {
        // A file removed meanwhile, or replaced by a newer pick, reports into nothing.
        if (!this.stagedFiles().some((staged) => staged.id === id)) {
          return;
        }
        if (event.status === 'uploading') {
          this.updateStagedFile(id, { progress: event.progress, status: 'uploading' });
          return;
        }
        this.updateStagedFile(id, { staging: event.staging, progress: 100, status: 'complete' });
        if (event.staging.istDuplikat) {
          this.duplicateWarning.set(true);
        }
      },
      error: (error: unknown) => {
        this.uploadSubscriptions.delete(id);
        const serverMessage = (error as HttpErrorResponse)?.error?.message;
        this.updateStagedFile(id, { status: 'failed', error: serverMessage ?? this.failedMessage });
      },
      complete: () => this.uploadSubscriptions.delete(id),
    });
    if (!subscription.closed) {
      this.uploadSubscriptions.set(id, subscription);
    }
  }

  /**
   * Fills the form from the document being edited, and leaves it at its initial values in
   * create mode. Runs on every open, so a second edit does not show the first one's values.
   */
  private seedFromUpload(): void {
    const editedUpload = this.upload();
    if (!editedUpload) {
      this.modus.set('datei');
      return;
    }

    this.modus.set(editedUpload.dateiTyp === 'link' ? 'link' : 'datei');
    this.uploadForm.dateiname().value.set(editedUpload.dateiname);
    this.uploadForm.beschreibung?.().value.set(editedUpload.beschreibung ?? null);
    this.uploadForm.externerLink?.().value.set(editedUpload.externerLink ?? null);
    this.uploadForm.tags().value.set((editedUpload.tags ?? []).map((tag) => tag.name));
    this.dokumentTypID.set(editedUpload.dokumentTyp?.uploadDokumentTypID ?? null);
    this.dokumentSubTypID.set(editedUpload.dokumentSubTyp?.uploadDokumentTypID ?? null);
  }

  /**
   * Chooses the segment a create opens on: the file segment with a file handed in, otherwise the
   * pick segment where there is one. Runs after {@link seedFromUpload}, which leaves an edit in the
   * segment of its document.
   */
  private openInitialSegment(): void {
    if (this.isEditMode()) {
      return;
    }
    const initialFiles = this.acceptsSeveralFiles() ? this.initialFiles() : this.initialFiles().slice(0, 1);
    if (initialFiles.length) {
      initialFiles.forEach((file) => void this.uploadFile(file));
      return;
    }
    if (this.hasPickSegment()) {
      this.modus.set('pick');
    }
  }

  /**
   * Loads the root document types for the dropdown, and the children of the type a stored
   * document carries. The user API has no type endpoint, so the picker is admin-only.
   *
   * @returns {Promise<void>}
   */
  private async loadDokumentTypes(): Promise<void> {
    if (!this.isAdmin()) {
      return;
    }
    try {
      const response = await lastValueFrom(uploadDokumentTypeControllerFindV1(this.httpClient, this.apiConfiguration.baseUrl));
      this.dokumentTypes.set(response.body);
    } catch {
      // The picker stays empty and the document is saved without a type; a toast over a
      // dialog being filled in would only get in the way.
      this.dokumentTypes.set([]);
    }
    await this.loadDokumentSubTypes(this.dokumentTypID());
  }

  /**
   * Loads the children of a type for the subtype dropdown.
   *
   * @param {string | null} parentID - The selected type; `null` leaves the dropdown empty
   * @returns {Promise<void>}
   */
  private async loadDokumentSubTypes(parentID: string | null): Promise<void> {
    if (!this.isAdmin() || parentID === null) {
      this.dokumentSubTypes.set([]);
      return;
    }
    try {
      const response = await lastValueFrom(
        uploadDokumentTypeControllerFindV1(this.httpClient, this.apiConfiguration.baseUrl, { parentId: parentID }),
      );
      // The user may have picked another type while this answer was on its way.
      if (parentID === this.dokumentTypID()) {
        this.dokumentSubTypes.set(response.body);
      }
    } catch {
      if (parentID === this.dokumentTypID()) {
        this.dokumentSubTypes.set([]);
      }
    }
  }

  /**
   * @param {LexplusUploadDokumentTypMiniDto[]} types - The offered types
   * @returns The types as `value` / `label` pairs for a `dm-select`
   */
  private toSelectOptions(types: LexplusUploadDokumentTypMiniDto[]): { value: string; label: string }[] {
    return types.map((type) => ({ value: type.uploadDokumentTypID, label: resolveDokumentTypLabel(type.labelKey) }));
  }

  /**
   * @param {LexplusUploadDokumentTypMiniDto[]} types - The offered types
   * @param {string | null} id - The selected ID
   * @returns The selected type, or `null` when none is selected
   */
  private findDokumentTyp(types: LexplusUploadDokumentTypMiniDto[], id: string | null): LexplusUploadDokumentTypMiniDto | null {
    return types.find((type) => type.uploadDokumentTypID === id) ?? null;
  }

  /**
   * Resolves a submitted selection to its type. An option list that has not loaded, or failed to,
   * has no match for a selection the user never touched, so that one comes from the stored document.
   *
   * @param {LexplusUploadDokumentTypMiniDto[]} types - The offered types
   * @param {string | null} id - The submitted ID
   * @param {LexplusUploadDokumentTypMiniDto | null | undefined} stored - What the document carried before the edit
   * @returns The submitted type, or `null` when none is selected or it cannot be resolved
   */
  private resolveSubmittedDokumentTyp(
    types: LexplusUploadDokumentTypMiniDto[],
    id: string | null,
    stored: LexplusUploadDokumentTypMiniDto | null | undefined,
  ): LexplusUploadDokumentTypMiniDto | null {
    if (id === null) {
      return null;
    }
    return this.findDokumentTyp(types, id) ?? (stored?.uploadDokumentTypID === id ? stored : null);
  }

  /**
   * Whether anything has been entered that a close would throw away. In edit mode the
   * seeded values are not «input», so only the form's own dirty state counts. A selection in the
   * pick segment counts as well.
   *
   * @returns {boolean} Whether the dialog holds unsaved input
   */
  private hasInput(): boolean {
    const dokumentTypChanged =
      this.dokumentTypID() !== (this.upload()?.dokumentTyp?.uploadDokumentTypID ?? null) ||
      this.dokumentSubTypID() !== (this.upload()?.dokumentSubTyp?.uploadDokumentTypID ?? null);
    return this.uploadForm().dirty() || dokumentTypChanged || this.stagedFiles().length > 0 || this.canSubmitPick();
  }

  /**
   * Drops files from the file tab, all of them unless some are named: aborts an upload still running
   * and deletes bytes that already arrived, so an abandoned file leaves nothing behind. A failing
   * delete is not surfaced — the user is on their way out, and the server prunes staging rows anyway.
   *
   * @param {ReadonlySet<string>} [ids] - The files to drop; all when omitted
   * @returns {Promise<void>}
   */
  private async discardStagedFiles(ids?: ReadonlySet<string>): Promise<void> {
    const discarded = this.stagedFiles().filter((staged) => !ids || ids.has(staged.id));
    discarded.forEach((staged) => {
      this.uploadSubscriptions.get(staged.id)?.unsubscribe();
      this.uploadSubscriptions.delete(staged.id);
    });
    this.stagedFiles.update((stagedFiles) => (ids ? stagedFiles.filter((staged) => !ids.has(staged.id)) : []));
    if (!this.stagedFiles().some((staged) => staged.staging?.istDuplikat)) {
      this.duplicateWarning.set(false);
    }

    await Promise.all(discarded.flatMap((staged) => (staged.staging ? [this.deleteStaging(staged.staging.uploadStagingID)] : [])));
  }

  /**
   * Deletes staged bytes nobody will store; see {@link discardStagedFiles} for why a failure is silent.
   *
   * @param {string} stagingId - The bytes to delete
   * @returns {Promise<void>}
   */
  private async deleteStaging(stagingId: string): Promise<void> {
    try {
      const kundeId = this.kundeId();
      await lastValueFrom(
        this.isAdmin() && kundeId !== null
          ? adminUploadsControllerDeleteDateiV1(this.httpClient, this.apiConfiguration.baseUrl, { stagingId, kundeId })
          : uploadsControllerDeleteDateiV1(this.httpClient, this.apiConfiguration.baseUrlUser, { stagingId }),
      );
    } catch {
      // Deliberately silent; see above.
    }
  }

  /**
   * The file a dropzone row action names, or every file for a full reset.
   *
   * @param {DmFileUploadItemAction} action - The row action
   * @returns {ReadonlySet<string> | undefined} The file's ID, or undefined for all of them
   */
  private idsOf(action: DmFileUploadItemAction): ReadonlySet<string> | undefined {
    return action.item?.id ? new Set([action.item.id]) : undefined;
  }

  /**
   * Patches one file. The array is replaced rather than mutated, because `files` reaches the
   * dropzone as a plain input and only a new reference does.
   *
   * @param {string} id - The file to patch
   * @param {Partial<StagedUploadFile>} patch - The fields that changed
   */
  private updateStagedFile(id: string, patch: Partial<StagedUploadFile>): void {
    this.stagedFiles.update((stagedFiles) => stagedFiles.map((staged) => (staged.id === id ? { ...staged, ...patch } : staged)));
  }
}
